/**
 * Auth API client for the Auteur Cloud web app.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C_AUTH. This is the browser half
 * of `auteur_cloud/api/routes/auth.py`, and its most important property is what
 * it does NOT do.
 *
 * THERE IS NO TOKEN IN THIS FILE
 *   The session credential is an `HttpOnly` cookie set by the server
 *   (`__Host-auteur_session`, attributes decided once in
 *   `auteur_cloud/auth/sessions.py`). Script cannot read it, this module never
 *   asks for it, and the login response body does not contain it. Consequently
 *   there is no `localStorage`, no `sessionStorage`, no in-memory token cache,
 *   and no `Authorization` header anywhere below: `CLOUD_SECURITY_AND_TENANCY_V1`
 *   rule I-7 names `localStorage`, `sessionStorage` and non-`HttpOnly` cookies
 *   as forbidden identity sources, and the only way to be sure a client never
 *   uses one is for the client never to hold a token at all.
 *
 *   Every call therefore sets `credentials: 'include'` and lets the browser
 *   attach the cookie. Session state is read back from `GET /v1/auth/session`,
 *   which answers from the server-side row — so a session revoked in another
 *   tab, or by an administrator, is gone on the next call rather than at the
 *   next expiry.
 *
 * ERRORS ARE NOT INTERPRETED
 *   `login()` rejects with the server's message verbatim for a 401. The server
 *   returns one identical body for an unknown address and a wrong password; a
 *   client that tried to be more helpful ("we don't recognise that email")
 *   would hand back the account-existence oracle the API just refused to be.
 *
 * BUILD STATUS
 *   Compiled and exercised as part of the Vite customer SPA.
 */

/** The one shape every endpoint here returns on failure. */
export interface AuthApiErrorBody {
  code: string;
  message: string;
  status: number;
}

/** What `GET /v1/auth/session` and `POST /v1/auth/login` describe. */
export interface AuthSession {
  org_id: string;
  principal_id: string;
  principal_kind: string;
  /** Present on `GET /v1/auth/session` only. */
  session_id?: string;
  auth_source?: string;
  verified_at?: string;
  /**
   * Present on `GET /v1/auth/session` only: true only for the deployment's
   * owner org, the one org the hosted Brain, Evals, Learning and tasks APIs serve.
   */
  owner_org?: boolean;
  /** Present on `POST /v1/auth/login` only. */
  expires_at?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface SignupRequest {
  email: string;
  password?: string;
}

export interface PasswordResetConfirmRequest {
  token: string;
  password: string;
  email?: string;
}

/** A structured failure. `retryAfterSeconds` is set only for a 429. */
export class AuthApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly retryAfterSeconds?: number;
  readonly waitlistUrl?: string;

  constructor(
    status: number,
    code: string,
    message: string,
    retryAfterSeconds?: number,
    waitlistUrl?: string,
  ) {
    super(message);
    this.name = 'AuthApiError';
    this.status = status;
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
    this.waitlistUrl = waitlistUrl;
  }

  /** True when the credential was refused — never why. */
  get isInvalidCredentials(): boolean {
    return this.status === 401;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

import { ApiClientError, apiUrl, requestJson, responseRecord } from './http';
import { clearPendingOffer } from '../lib/pendingOffer';
import { forgetSignedInAccount, rememberSignedInAccount } from '../lib/signedInAccount';

const AUTH_BASE = '/v1/auth';

/**
 * One fetch wrapper, so `credentials: 'include'` is decided once.
 *
 * `same-origin` would also work for the first-party deployment, but `include`
 * is explicit about the intent and correct when the API is served from a
 * sibling origin. Either way the cookie is `SameSite=Lax`, so a cross-site
 * request does not carry it whatever this argument says — the browser, not
 * this line, is the control.
 */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  try {
    return (await requestJson(`${AUTH_BASE}${path}`, init)) as T;
  } catch (error) {
    if (error instanceof ApiClientError) {
      throw new AuthApiError(error.status, error.code, error.message);
    }
    throw error;
  }
}

function sessionFrom(payload: unknown): AuthSession {
  const session = responseRecord(payload, 'session');
  for (const field of ['org_id', 'principal_id', 'principal_kind']) {
    if (typeof session[field] !== 'string') {
      throw new AuthApiError(200, 'malformed_response', `session.${field} is missing`);
    }
  }
  return session as unknown as AuthSession;
}

/**
 * Sign in. Resolves with the session description; the credential itself
 * arrives as an `HttpOnly` cookie the browser stores and this code never sees.
 */
export async function login(credentials: LoginRequest): Promise<AuthSession> {
  const body = await request<unknown>('/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
  const session = sessionFrom(body);
  // Display only (F-38): the top bar names the account this session belongs to.
  rememberSignedInAccount(session, credentials.email);
  return session;
}

/**
 * Request an account. Admin-created invites need only an email; public GoTrue
 * signup modes ask for a password after the server reports that requirement.
 * The invite-only response carries a server-selected waitlist URL, so this
 * action reads that one extra field while preserving the server's message.
 */
export async function signup(credentials: SignupRequest): Promise<string> {
  const controller = new AbortController();
  const timer = globalThis.setTimeout(() => controller.abort(), 15_000);
  try {
    let response: Response;
    try {
      response = await fetch(apiUrl(AUTH_BASE + '/signup'), {
        method: 'POST',
        credentials: 'include',
        signal: controller.signal,
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          email: credentials.email,
          ...(credentials.password !== undefined ? { password: credentials.password } : {}),
        }),
      });
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === 'AbortError') {
        throw new AuthApiError(0, 'request_timeout', 'the API request timed out');
      }
      if (caught instanceof ApiClientError) {
        throw new AuthApiError(caught.status, caught.code, caught.message);
      }
      throw new AuthApiError(0, 'network_error', 'the API could not be reached');
    }

    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      if (!response.ok) {
        throw new AuthApiError(
          response.status,
          'request_failed',
          'the API returned HTTP ' + response.status,
        );
      }
      throw new AuthApiError(
        response.status,
        'malformed_response',
        'the API returned a malformed JSON response',
      );
    }

    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      throw new AuthApiError(200, 'malformed_response', 'the API response is malformed');
    }
    const record = payload as Record<string, unknown>;
    if (!response.ok) {
      const error = record.error && typeof record.error === 'object' && !Array.isArray(record.error)
        ? record.error as Record<string, unknown>
        : null;
      const code = typeof error?.code === 'string' ? error.code : 'request_failed';
      const message = typeof error?.message === 'string'
        ? error.message
        : 'the API returned HTTP ' + response.status;
      const waitlistUrl = typeof error?.waitlist_url === 'string' ? error.waitlist_url : undefined;
      throw new AuthApiError(response.status, code, message, undefined, waitlistUrl);
    }
    if (typeof record.message !== 'string') {
      throw new AuthApiError(
        response.status,
        'malformed_response',
        'the API response is missing its message',
      );
    }
    return record.message;
  } catch (caught) {
    if (caught instanceof AuthApiError) throw caught;
    if (caught instanceof ApiClientError) {
      throw new AuthApiError(caught.status, caught.code, caught.message);
    }
    throw caught;
  } finally {
    globalThis.clearTimeout(timer);
  }
}

/**
 * Sign out. Revokes the session server-side — not merely locally — so a copy
 * of the cookie kept anywhere else stops working immediately.
 */
export async function logout(): Promise<void> {
  await request<{ logged_out: boolean }>('/logout', { method: 'POST' });
  // A plan chosen before sign-out must not carry over to the next person, nor the address shown.
  clearPendingOffer();
  forgetSignedInAccount();
}

/**
 * Read the current session, or `null` when there is none.
 *
 * A 401 is the ordinary answer for a signed-out visitor, so it resolves to
 * `null` rather than rejecting; every other failure still throws.
 */
export async function currentSession(): Promise<AuthSession | null> {
  try {
    const body = await request<unknown>('/session', { method: 'GET' });
    return sessionFrom(body);
  } catch (error) {
    if (error instanceof AuthApiError && error.status === 401) {
      return null;
    }
    throw error;
  }
}

/** Ask for a reset link. The message is fixed for every address. */
export async function requestPasswordReset(email: string): Promise<string> {
  const body = responseRecord(await request<unknown>('/password-reset/request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  }));
  if (typeof body.message !== 'string') {
    throw new AuthApiError(200, 'malformed_response', 'the API response is missing its message');
  }
  return body.message;
}

/**
 * Complete a reset. Returns how many live sessions the change revoked and
 * whether this created the account. It does NOT sign the caller in: the token
 * is never a session. The page then signs in with the password the person just
 * typed, which is the ordinary login anyone holding that password could do.
 */
export async function completePasswordReset(
  input: PasswordResetConfirmRequest,
): Promise<PasswordResetOutcome> {
  const body = responseRecord(await request<unknown>('/password-reset/confirm', {
    method: 'POST',
    body: JSON.stringify(input),
  }));
  if (typeof body.sessions_revoked !== 'number') {
    throw new AuthApiError(200, 'malformed_response', 'the reset response is malformed');
  }
  // An older server does not say; treat that as an ordinary reset.
  return { sessionsRevoked: body.sessions_revoked, accountCreated: body.account_created === true };
}

export interface PasswordResetOutcome {
  sessionsRevoked: number;
  /** True when this set an invited person's first password (their organization was just created). */
  accountCreated: boolean;
}

/** Ask for a verification link. Fixed message, as with reset. */
export async function requestEmailVerification(email: string): Promise<string> {
  const body = responseRecord(await request<unknown>('/email-verification/request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  }));
  if (typeof body.message !== 'string') {
    throw new AuthApiError(200, 'malformed_response', 'the API response is missing its message');
  }
  return body.message;
}

/** Confirm an address from an emailed token. Mints no session. */
export async function confirmEmailVerification(
  token: string,
  email?: string,
): Promise<boolean> {
  const body = responseRecord(await request<unknown>('/email-verification/confirm', {
    method: 'POST',
    body: JSON.stringify(email ? { token, email } : { token }),
  }));
  if (typeof body.verified !== 'boolean') {
    throw new AuthApiError(200, 'malformed_response', 'the verification response is malformed');
  }
  return body.verified;
}
